import React, { useState, useEffect, useCallback } from 'react';
import { webhooksApi } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Plus, Trash, Pencil, Play, Globe, ArrowUp, ArrowDown, Check, X, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function WebhooksPage() {
  const [webhooks, setWebhooks] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', url: '', events: [] });

  const fetchData = useCallback(async () => {
    try {
      const [whRes, evRes, inRes] = await Promise.all([
        webhooksApi.getAll(),
        webhooksApi.getEvents(),
        webhooksApi.getIncoming({ limit: 20 }).catch(() => ({ data: [] }))
      ]);
      setWebhooks(whRes.data);
      setAllEvents(evRes.data.events || []);
      setIncoming(inRes.data || []);
    } catch (error) {
      toast.error('Failed to load webhooks');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await webhooksApi.update(editing.id, formData);
        toast.success('Webhook updated');
      } else {
        await webhooksApi.create(formData);
        toast.success('Webhook created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this webhook?')) return;
    try { await webhooksApi.delete(id); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const handleTest = async (id) => {
    try {
      const res = await webhooksApi.test(id);
      if (res.data.success) toast.success(`Test successful (${res.data.status_code})`);
      else toast.error(`Test failed (${res.data.status_code})`);
    } catch { toast.error('Test failed'); }
  };

  const handleToggle = async (wh) => {
    try { await webhooksApi.update(wh.id, { is_active: !wh.is_active }); fetchData(); } catch { toast.error('Failed'); }
  };

  const viewLogs = async (wh) => {
    setSelectedWebhook(wh);
    try {
      const res = await webhooksApi.getLogs(wh.id, { limit: 30 });
      setSelectedLogs(res.data);
    } catch { toast.error('Failed to load logs'); }
  };

  const openEdit = (wh) => {
    setEditing(wh);
    setFormData({ name: wh.name, url: wh.url, events: wh.events || [] });
    setIsDialogOpen(true);
  };

  const resetForm = () => { setEditing(null); setFormData({ name: '', url: '', events: [] }); };

  const toggleEvent = (event) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  const incomingUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/incoming/`;

  return (
    <div className="space-y-6" data-testid="webhooks-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Webhooks</h1>
          <p className="text-muted-foreground text-sm mt-1">Connect with external tools via webhooks</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="rounded-sm" data-testid="add-webhook-btn">
          <Plus size={18} className="mr-2" /> New Webhook
        </Button>
      </div>

      <Tabs defaultValue="outgoing">
        <TabsList>
          <TabsTrigger value="outgoing"><ArrowUp size={16} className="mr-1" /> Outgoing</TabsTrigger>
          <TabsTrigger value="incoming"><ArrowDown size={16} className="mr-1" /> Incoming</TabsTrigger>
        </TabsList>

        <TabsContent value="outgoing" className="space-y-4">
          {webhooks.length === 0 ? (
            <div className="text-center p-12 border border-dashed border-border rounded-sm">
              <Globe size={40} className="mx-auto text-muted-foreground mb-3 opacity-40" />
              <p className="text-muted-foreground">No outgoing webhooks configured</p>
            </div>
          ) : webhooks.map(wh => (
            <div key={wh.id} className="bg-card border border-border p-4 rounded-sm" data-testid={`webhook-${wh.id}`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{wh.name || 'Unnamed'}</h3>
                    <Badge variant={wh.is_active ? 'default' : 'secondary'}>{wh.is_active ? 'Active' : 'Inactive'}</Badge>
                    {wh.last_status && <Badge variant="outline" className="text-xs">Last: {wh.last_status}</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground font-mono truncate mt-1">{wh.url}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {wh.events?.slice(0, 5).map(ev => <Badge key={ev} variant="outline" className="text-xs">{ev}</Badge>)}
                    {wh.events?.length > 5 && <Badge variant="outline" className="text-xs">+{wh.events.length - 5}</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Switch checked={wh.is_active} onCheckedChange={() => handleToggle(wh)} />
                  <Button variant="outline" size="sm" onClick={() => handleTest(wh.id)} data-testid={`test-${wh.id}`}><Play size={14} className="mr-1" /> Test</Button>
                  <Button variant="ghost" size="sm" onClick={() => viewLogs(wh)}>Logs</Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(wh)}><Pencil size={14} /></Button>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(wh.id)}><Trash size={14} /></Button>
                </div>
              </div>
            </div>
          ))}

          {selectedWebhook && (
            <div className="bg-card border border-border p-4 rounded-sm">
              <h3 className="font-semibold mb-3">Delivery Logs: {selectedWebhook.name}</h3>
              <div className="space-y-2">
                {selectedLogs.length === 0 ? <p className="text-muted-foreground text-sm">No logs</p> :
                  selectedLogs.map(log => (
                    <div key={log.id} className="flex items-center gap-3 p-2 bg-muted/50 rounded-sm text-sm">
                      {log.success ? <Check size={16} className="text-success" /> : <X size={16} className="text-destructive" />}
                      <Badge variant="outline" className="text-xs">{log.event}</Badge>
                      <span className="text-muted-foreground">Status: {log.status_code}</span>
                      <span className="text-muted-foreground text-xs ml-auto">{formatDateTime(log.created_at)}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="incoming" className="space-y-4">
          <div className="bg-card border border-border p-4 rounded-sm">
            <h3 className="font-semibold mb-2">Incoming Webhook URL</h3>
            <div className="flex items-center gap-2 p-2 bg-muted rounded-sm">
              <code className="text-sm flex-1 truncate">{incomingUrl}&#123;channel&#125;</code>
              <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(incomingUrl + 'default'); toast.success('Copied'); }}><Copy size={14} /></Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Replace &#123;channel&#125; with your integration name (e.g., zapier, slack)</p>
          </div>
          <div className="bg-card border border-border rounded-sm overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-border bg-muted/50">
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Channel</th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Payload</th>
                <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Received</th>
              </tr></thead>
              <tbody>
                {incoming.length === 0 ? <tr><td colSpan={3} className="p-8 text-center text-muted-foreground">No incoming webhooks received</td></tr> :
                  incoming.map(inc => (
                    <tr key={inc.id} className="border-b border-border">
                      <td className="p-3 font-medium">{inc.channel}</td>
                      <td className="p-3 text-xs font-mono text-muted-foreground max-w-xs truncate">{JSON.stringify(inc.payload).slice(0, 100)}</td>
                      <td className="p-3 text-sm text-muted-foreground">{formatDateTime(inc.created_at)}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Webhook' : 'New Webhook'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Slack Notifications" data-testid="webhook-name" />
            </div>
            <div className="space-y-2">
              <Label>URL *</Label>
              <Input value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} placeholder="https://hooks.example.com/webhook" required data-testid="webhook-url" />
            </div>
            <div className="space-y-2">
              <Label>Events</Label>
              <ScrollArea className="h-48 border border-border rounded-sm p-3">
                <div className="space-y-2">
                  {allEvents.map(event => (
                    <div key={event} className="flex items-center gap-2">
                      <Checkbox checked={formData.events.includes(event)} onCheckedChange={() => toggleEvent(event)} id={`ev-${event}`} />
                      <label htmlFor={`ev-${event}`} className="text-sm cursor-pointer">{event}</label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <p className="text-xs text-muted-foreground">{formData.events.length} events selected</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="webhook-submit">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
